import { DEFAULT_TIMERANGE, TIMEFRAME } from "./timeFrame";
import type {
  ChartDisplaySettings,
  DrawingState,
  IndicatorState,
  MarketSelection,
  State,
  Timeframe,
  TradeState,
} from "./types";

export const GUTTERS = {
  left: 3,
  right: 60,
  top: 43,
  bottom: 43,
};

export function createChartDisplaySettings(): ChartDisplaySettings {
  return {
    showTimeGrid: true,
    showPriceGrid: true,
    showTimeAxis: true,
    showPriceAxis: true,
    showCrosshair: true,
    showLivePriceLine: true,
  };
}

export function createMarketSelection(symbol = "VIX_10"): MarketSelection {
  return { symbol };
}

export function createIndicatorState(): IndicatorState {
  return {
    indicatorType: "none",
    indicatorPeriod: 20,
    indicatorColor: "#1d4ed8",
    indicatorLineWidth: 2,
    indicators: [],
  };
}

export function createDrawingState(): DrawingState {
  return {
    activeDrawingTool: null,
    drawings: [],
    currentDrawing: null,
    selectedDrawingId: null,
    hoveredDrawingId: null,
    draggingDrawing: null,
  };
}

export function createTradeState(): TradeState {
  return {
    trades: [],
    tradeStyles: {
      pin: true,
      vertical: true,
      zone: true,
      countdown: true,
      animation: true,
    },
  };
}

export function createState(width: number, height: number): State {
  const now = Date.now();
  const tf = TIMEFRAME[DEFAULT_TIMERANGE];
  const indicators = createIndicatorState();
  const drawings = createDrawingState();
  const trades = createTradeState();

  return {
    width,
    height,

    timeStart: now - tf.defaultRange,
    timeEnd: now,
    timeZoomLevel: 0,

    priceMin: 980,
    priceMax: 1020,
    priceZoomLevel: 0,

    left: GUTTERS.left,
    right: GUTTERS.right,
    top: GUTTERS.top,
    bottom: GUTTERS.bottom,

    timeframe: DEFAULT_TIMERANGE,
    themeName: "light",
    isDragging: false,
    zoomCooldown: 16,
    zoomLastTime: 0,

    chartData: [],
    chartType: "area",
    indicatorType: indicators.indicatorType,
    indicatorPeriod: indicators.indicatorPeriod,
    indicatorColor: indicators.indicatorColor,
    indicatorLineWidth: indicators.indicatorLineWidth,
    indicators: indicators.indicators,
    activeDrawingTool: drawings.activeDrawingTool,
    drawings: drawings.drawings,
    currentDrawing: drawings.currentDrawing,
    selectedDrawingId: drawings.selectedDrawingId,
    hoveredDrawingId: drawings.hoveredDrawingId,
    draggingDrawing: drawings.draggingDrawing,
    trades: trades.trades,
    tradeStyles: trades.tradeStyles,
    displaySettings: createChartDisplaySettings(),
    useDataRange: true,
    tightPriceScale: false,
    crosshairX: 0,
    crosshairY: 0,
    crosshairInside: false,
    selectedContractType: undefined,
    fitBarriersToView: true,
    barrierTopMargin: 80,
    barrierBottomMargin: 40,
    pricePosition: "right",
    isMarketLive: false,
    marketStatus: "unknown" as "open" | "closed" | "unknown",
  };
}

export const createChartState = createState;
export const createChartIndicators = createIndicatorState;
export const createChartDrawings = createDrawingState;
export const createChartTrades = createTradeState;

export function plotWidth(state: State): number {
  return state.width - state.left - state.right;
}

export function plotHeight(state: State): number {
  return state.height - state.top - state.bottom;
}

export function setTimeframeState(state: State, timeframe: Timeframe): void {
  state.timeframe = timeframe;
  state.timeZoomLevel = 0;
  state.timeStart = Date.now() - TIMEFRAME[timeframe].defaultRange;
  state.timeEnd = Date.now();
  state.priceZoomLevel = 0;
  state.priceMin = 980;
  state.priceMax = 1020;
}
